import { ApplicantRole } from '../../../../../../types/PartyApplication/partyApplication';
import {
  EmployeeRow,
  EmployeeRowFilter,
  TerminatedEmployeeField,
  TerminatedEmployeeInput,
} from '../types';
import { createEmptyEmployee } from './createEmptyEmployee';
import { getEmployeeRowStatus } from './getEmployeeRowStatus';
import { getEmployeeSearchText } from './getEmployeeSearchText';
import {
  getEmployeeWarnings,
  TerminatedEmployeesWarningMessages,
} from './getEmployeeWarnings';

export const EMPLOYEE_FIELDS: TerminatedEmployeeField[] = [
  'socialSecurityNumber',
  'firstName',
  'lastName',
  'employmentType',
  'startDate',
  'terminationDate',
  'endDate',
  'email',
  'phone',
];

type RowFieldErrors = Record<string, { message?: unknown } | undefined>;

/**
 * Single source of truth for the section: pairs the stable field id with the current
 * values, the react-hook-form errors and the derived warnings/status.
 */
export const buildEmployeeRows = (
  fields: { id: string }[],
  values: TerminatedEmployeeInput[] | undefined,
  errors: (RowFieldErrors | undefined)[] | undefined,
  applicantRole?: ApplicantRole,
  today?: Date,
  warningMessages?: Partial<TerminatedEmployeesWarningMessages>
): EmployeeRow[] =>
  fields.map((field, formIndex) => {
    const employee: TerminatedEmployeeInput = {
      ...createEmptyEmployee(),
      ...values?.[formIndex],
    };

    const fieldErrors = errors?.[formIndex];
    const rowErrors: EmployeeRow['errors'] = {};
    EMPLOYEE_FIELDS.forEach((name) => {
      const message = fieldErrors?.[name]?.message;
      if (typeof message === 'string' && message.length > 0) {
        rowErrors[name] = message;
      }
    });

    const warnings = getEmployeeWarnings(
      employee,
      today,
      applicantRole,
      warningMessages
    );

    return {
      fieldId: field.id,
      formIndex,
      employee,
      errors: rowErrors,
      warnings,
      status: getEmployeeRowStatus(
        Object.keys(rowErrors).length,
        warnings.length
      ),
      searchText: getEmployeeSearchText(employee),
    };
  });

export const countRowsByStatus = (rows: EmployeeRow[]) => ({
  total: rows.length,
  errors: rows.filter((row) => row.status === 'error').length,
  warnings: rows.filter((row) => row.warnings.length > 0).length,
});

export const filterEmployeeRows = (
  rows: EmployeeRow[],
  filter: EmployeeRowFilter,
  search: string
): EmployeeRow[] => {
  const term = search.trim().toLowerCase();
  return rows.filter((row) => {
    if (filter === 'errors' && row.status !== 'error') {
      return false;
    }
    if (filter === 'warnings' && row.warnings.length === 0) {
      return false;
    }
    return term.length === 0 || row.searchText.includes(term);
  });
};
